import { Fragment, useMemo, useState, type FC } from 'react';

import { cn } from '@repo/utils/common';

import type { MachineGroup, MachineSubgroup } from '@/shared/types/machine.types';

import { useMediaQuery } from '@/client/hooks/media.hooks';

import { MachineGroupAccordion } from '@/components/machine/MachineGroupAccordion';
import { MachineGroupCard } from '@/components/machine/MachineGroupCard';

interface MachineGroupGridProps {
  className?: string;
  groups: MachineGroup[];
}

export const MachineGroupGrid: FC<MachineGroupGridProps> = ({ className, groups }) => {
  const isTwoCols = useMediaQuery('(min-width: 1024px)');
  const [selectedGroup, setSelectedGroup] = useState<MachineGroup | null>(null);
  const [selectedSubgroup, setSelectedSubgroup] = useState<MachineSubgroup | null>(null);

  // when there are two machines in a row, we shift the index by 1,
  // to display the selection table in a row below the two machines
  const fixedIndex = useMemo(() => {
    const selIndex = groups.findIndex((m) => m.id === selectedGroup?.id);
    if (selIndex < 0) return -1;
    // shift only when there is a sibling in the same row
    const canShift = selIndex + 1 < groups.length;
    return !isTwoCols || !canShift || selIndex % 2 !== 0 ? selIndex : selIndex + 1;
  }, [groups, isTwoCols, selectedGroup]);

  return (
    <div className={cn('grid grid-cols-1 gap-4 lg:grid-cols-2', className)}>
      {groups.map((group, index) => (
        <Fragment key={group.id}>
          <MachineGroupCard
            group={group}
            isSelected={selectedGroup?.id === group.id}
            selectedSubgroup={selectedSubgroup}
            onClick={() => {
              const nextGroup = selectedGroup?.id === group.id ? null : group;
              setSelectedGroup(nextGroup);
              setSelectedSubgroup(nextGroup?.subgroups[0] ?? null);
            }}
            onSubgroupClick={(subgroup) => {
              setSelectedGroup(group);
              setSelectedSubgroup(subgroup);
            }}
          />

          {selectedGroup && fixedIndex === index && (
            <MachineGroupAccordion
              group={selectedGroup}
              selectedSubgroup={selectedSubgroup}
              onSubgroupChange={setSelectedSubgroup}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
};
